import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Form, Button, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Formik, ErrorMessage, Field, FieldArray } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { jwtDecode } from "jwt-decode";
import DropFileInput from "./drag-file-input";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import FocusError from "../../../components/error/error-focus";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import InputField from "../../../components/input/input-field";
import { RxCross2 } from "react-icons/rx";
import { FaPlus } from "react-icons/fa";
import { roleTypes } from "../../../data/roleType";

const ProductAdd = () => {
  const alertRef = useRef(null);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [phases, setPhases] = useState([]);
  const [decodedToken, setDecodedToken] = useState();
  const token = localStorage.getItem("coyote-token");
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });
  const [formData, setFormData] = useState({
    name: "",
    sku: "",
    categoryId: "",
    manufacturerId: null,
    model: "",
    upcNumber: "",
    inventoryType: "standard",
    msrp: "",
    cost: "",
    labourPerHourPerunit: "",
    rmu: "",
    backbox: "",
    technicalSpecs: [{ key: "", value: "" }],
    description: "",
    unitOfMeasure: "",
    weight: "",
    vendorId: "",
    dimensions: [{ key: "", value: "" }],
    phases: [null],
    productPictures: [],
  });

  const getManufacturers = async () => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(
        `${url.backendURL}manufacturer/list`,
        config
      );
      setManufacturers(response.data.response.data);
    } catch (err) {
      handleError(err, "product-add");
    }
  };

  const getVendors = async () => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(`${url.backendURL}vendor/all`, config);
      setVendors(response.data.response.data);
    } catch (err) {
      handleError(err, "product-add");
    }
  };

  useEffect(() => {
    const getCategories = async () => {
      try {
        const config = {
          headers: {
            Authorization: `${token}`,
          },
        };
        const response = await axios.get(
          `${url.backendURL}category/all`,
          config
        );
        setCategories(response.data.response.data);
      } catch (err) {
        handleError(err, "product-add");
      }
    };
    const getPhases = async () => {
      try {
        const config = {
          headers: {
            Authorization: `${token}`,
          },
        };
        const response = await axios.get(
          `${url.backendURL}phase/all`,
          config
        );
        setPhases(response.data.response.data);
      } catch (err) {
        handleError(err, "product-add");
      }
    };
    getManufacturers();
    getCategories();
    getVendors();
    getPhases();
  }, [decodedToken]);

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (
      response.data.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${decodedToken?.roles?.some(
          (role) => role.role_type === roleTypes.SUPER_ADMIN
        )
          ? "admin"
          : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  useEffect(() => {
    if (token && typeof token === "string") {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    const formData = new FormData();
    Object.keys(values).forEach((key) => {
      if (key !== "productPictures") {
        if (key === "technicalSpecs" || key === "dimensions") {
          const filteredData = values[key].filter(
            (item) => item.key.trim() !== "" || item.value.trim() !== ""
          );

          if (filteredData.length > 0) {
            formData.append(key, JSON.stringify(filteredData));
          }
        } else if (key === "phases") {
          const filteredData = values[key].filter(
            (item) => item !== "" && item !== null
          );

          if (filteredData.length > 0) {
            formData.append(key, JSON.stringify(filteredData));
          }
        } else if (key === "categoryId") {
          const categoryValue = values[key];
          if (
            categoryValue === "" ||
            categoryValue === null ||
            categoryValue === undefined
          ) {
            formData.append(key, "");
          } else {
            formData.append(key, Number(categoryValue));
          }
        } else {
          const value = values[key];
          if (value !== null && value !== undefined) {
            formData.append(key, value);
          }
        }
      }
    });

    if (values.productPictures && values.productPictures.length > 0) {
      for (let i = 0; i < values.productPictures.length; i++) {
        formData.append("productPictures", values.productPictures[i]);
      }
    }

    try {
      const config = {
        headers: {
          Authorization: `${token}`,
          "Content-Type": "multipart/form-data",
        },
      };

      const response = await axios.post(
        `${url.backendURL}product/`,
        formData,
        config
      );
      setErrors({});
      if (alertRef.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      setAlert({
        message: response.data.message,
        type: "success",
        show: true,
        alertFor: "product-add",
      });

      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
        navigate(`/dashboard/app/product-list`);
      }, 2000);
    } catch (err) {
      handleError(err, "product-add");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = (resetForm) => {
    resetForm();
    setErrors({});
    setFormData({
      name: "",
      sku: "",
      categoryId: null,
      manufacturerId: null,
      model: "",
      upcNumber: "",
      inventoryType: "standard",
      msrp: "",
      cost: "",
      labourPerHourPerunit: "",
      rmu: "",
      backbox: "",
      technicalSpecs: [{ key: "", value: "" }],
      description: "",
      unitOfMeasure: "",
      weight: "",
      vendorId: null,
      dimensions: [{ key: "", value: "" }],
      productPictures: [],
    });
  };

  const validationSchema = () =>
    Yup.object().shape({
      productPictures: Yup.array()
        .test(
          "fileType",
          "All files must be valid images (jpeg, png, jpg)",
          (value) => {
            if (value && value.length > 0) {
              return value.every((file) =>
                ["image/png", "image/jpg", "image/jpeg"].includes(file.type)
              );
            }
            return true;
          }
        )
        .nullable()
        .notRequired(),
      name: Yup.string()
        .matches(
          /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
          "Product name must contain at least one letter or number"
        )
        .min(1, "Product name must be at least 1 character long")
        .max(255, "Product name must not exceed 255 characters")
        .trim()
        .required("Product Name is required"),
      categoryId: Yup.number()
        .integer()
        .nullable()
        .notRequired()
        .typeError("Category is invalid")
        .transform((value) => (isNaN(value) ? null : value)),
      model: Yup.string()
        .trim()
        .matches(
          /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
          "Model name must contain at least one letter or number"
        )
        .min(1, "Model name must be at least 1 character long")
        .max(100, "Model name must not exceed 100 characters")
        .required("Model name is required"),
      sku: Yup.string()
        .trim()
        .matches(
          /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
          "SKU must contain at least one letter or number"
        )
        .max(100, "SKU must not exceed 100 characters")
        .required("SKU is required"),
      inventoryType: Yup.string()
        .oneOf(["standard", "serial", "no_inventory"], "Invalid inventory type")
        .required("Inventory type is required"),
      upcNumber: Yup.string()
        .matches(/^\d+$/, "UPC number must contain only digits")
        .min(10, "UPC number must be at least 10 digits")
        .max(12, "UPC number must be at most 12 digits")
        .nullable()
        .notRequired(),

      msrp: Yup.number()
        .nullable()
        .notRequired()
        .typeError("MSRP must be a number")
        .transform((value) =>
          value === "" || value === undefined ? null : value
        )
        .test(
          "max-10-digits-2-decimal",
          "MSRP must be a number with up to 10 digits including up to 2 decimal places",
          (value) => {
            if (value === null || value === undefined || value === "")
              return true;
            return (
              /^\d{1,10}(\.\d{1,2})?$/.test(String(value)) &&
              Number(value) <= 9999999999.99
            );
          }
        ),

      cost: Yup.number()
        .required("Cost is required")
        .typeError("Cost must be a number")
        .transform((value) =>
          value === "" || value === undefined ? null : value
        )
        .test(
          "max-10-digits-2-decimal",
          "Cost must be a number with up to 10 digits including up to 2 decimal places",
          (value) => {
            if (value === null || value === undefined || value === "")
              return false;
            return (
              /^\d{1,10}(\.\d{1,2})?$/.test(String(value)) &&
              Number(value) <= 9999999999.99
            );
          }
        ),

      unitOfMeasure: Yup.string()
        .trim()
        .matches(
          /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
          "Unit of measure is invalid"
        )
        .max(50, "Unit of measure must not exceed 50 characters")
        .required("Unit of Measure is required"),

      labourPerHourPerunit: Yup.number()
        .nullable()
        .notRequired()
        .typeError("Labour per hour per unit must be a number")
        .transform((value) => (value === "" ? null : value))
        .test(
          "is-decimal",
          "Labour per hour per unit must have a maximum of 2 decimal places",
          (value) => {
            if (value === null || value === undefined || value === "") {
              return true;
            }
            return /^[+-]?\d+(\.\d{1,2})?$/.test(String(value));
          }
        ),

      rmu: Yup.string()
        .matches(
          /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
          "RMU must contain at least one letter or number"
        )
        .max(100, "RMU must not exceed 100 characters")
        .nullable()
        .notRequired(),

      backbox: Yup.string()
        .matches(
          /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
          "Backbox must contain at least one letter or number"
        )
        .max(100, "Backbox must not exceed 100 characters")
        .nullable()
        .notRequired(),
      description: Yup.string()
        .trim()
        .max(5000, "Description must not exceed 5000 characters")
        .nullable()
        .notRequired(),
      manufacturerId: Yup.number()
        .integer()
        .required("Manufacturer is required")
        .typeError("Manufacturer is invalid"),
      vendorId: Yup.number()
        .integer()
        .required("Vendor is required")
        .typeError("Vendor is invalid"),
      weight: Yup.number()
        .nullable()
        .notRequired()
        .typeError("Weight must be a number")
        .transform((value) => (value === "" ? null : value))
        .test(
          "is-decimal",
          "Weight must have a maximum of 2 decimal places",
          (value) => {
            if (value === null || value === undefined || value === "") {
              return true;
            }
            return /^[+-]?\d+(\.\d{1,2})?$/.test(String(value));
          }
        ),

      technicalSpecs: Yup.array()
        .of(
          Yup.object().shape({
            key: Yup.string().test(
              "key-required-if-value",
              "Name is required when value is provided",
              function (value) {
                const { value: fieldValue } = this.parent;
                if (fieldValue && fieldValue.length > 0) {
                  return value && value.length > 0;
                }
                return true;
              }
            ),
            value: Yup.string().test(
              "value-required-if-key",
              "Value is required when name is provided",
              function (value) {
                const { key } = this.parent;
                if (key && key.length > 0) {
                  return value && value.length > 0;
                }
                return true;
              }
            ),
          })
        )
        .nullable()
        .notRequired(),

      dimensions: Yup.array()
        .of(
          Yup.object().shape({
            key: Yup.string().test(
              "key-required-if-value",
              "Name is required when value is provided",
              function (value) {
                const { value: fieldValue } = this.parent;
                if (fieldValue && fieldValue.length > 0) {
                  return value && value.length > 0;
                }
                return true;
              }
            ),
            value: Yup.string().test(
              "value-required-if-key",
              "Value is required when name is provided",
              function (value) {
                const { key } = this.parent;
                if (key && key.length > 0) {
                  return value && value.length > 0;
                }
                return true;
              }
            ),
          })
        )
        .nullable()
        .notRequired(),
      phases: Yup.array()
        .of(
          Yup.number()
            .nullable()
            .test("required-first-phase", "Phase is required", function (value) {
              const { path } = this;
              const match = path.match(/\[(\d+)\]/);
              const index = match ? parseInt(match[1], 10) : -1;
              if (index === 0 && (value === "" || value === null || value === undefined)) {
                return false;
              }
              return true;
            })
        ),
    });

  const [options, setOptions] = useState([]);

  useEffect(() => {
    const categoryOptions =
      categories.length > 0 &&
      categories.map((category) => ({
        value: category.id,
        label: category.name,
      }));
    setOptions(categoryOptions);
  }, [categories]);

  const [optionsPhase, setOptionsPhase] = useState([]);

  useEffect(() => {
    if (phases) {
      const phaseOptions =
        phases.length > 0 &&
        phases.map((phase) => ({
          value: phase.id,
          label: phase.name,
        }));
      setOptionsPhase(phaseOptions || []);
    }
  }, [phases]);

  const [optionsManufacturer, setOptionsManufacturer] = useState([]);

  useEffect(() => {
    const manufacturerOptions =
      manufacturers.length > 0 &&
      manufacturers.map((manufacturer) => ({
        value: manufacturer.id,
        label: manufacturer.name,
      }));
    setOptionsManufacturer(manufacturerOptions || []);
  }, [manufacturers]);

  const [optionsVendor, setOptionsVendor] = useState([]);

  useEffect(() => {
    const vendorOptions =
      vendors.length > 0 &&
      vendors.map((vendor) => ({
        value: vendor.id,
        label: vendor.name,
      }));
    setOptionsVendor(vendorOptions);
  }, [vendors]);

  return (
    <div>
      <Formik
        enableReinitialize={true}
        initialValues={formData}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          resetForm,
          setFieldValue,
        }) => (
          <Form onSubmit={handleSubmit}>
            <FocusError />

            <Row>
              <Col md="12" ref={alertRef}>
                <Card>
                  <Card.Header className="d-flex justify-content-between">
                    <div
                      className="breadcrumb header-title"
                      style={{ cursor: "pointer" }}
                    >
                      <h4
                        onClick={() => navigate("/dashboard/app/product-list")}
                        className="text-primary card-title"
                        style={{ cursor: "pointer" }}
                      >
                        <span>General Product</span>
                      </h4>
                      <h4 className="mx-2 card-title">{">"}</h4>
                      <h4 className="card-title">Add</h4>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <AlertNotificationSimple
                      type={alert.type}
                      message={alert.message}
                      show={alert.show}
                      onClose={() => setAlert({ ...alert, show: false })}
                    />

                    <div className="new-product-info">
                      <div className="row">
                        <div className="col-12 mb-4">
                          <div className="border border-primary rounded p-3">
                            <h5 className="text-primary mb-3 pb-2 ">
                              Required Information{" "}
                              <span className="text-danger">*</span>
                            </h5>
                            <div className="row">
                              <InputField
                                className="col-md-6 form-group"
                                type="text"
                                name="name"
                                placeholder="Enter product name"
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                asterick={true}
                                errorMessage={errors?.name}
                                label="Product Name"
                              />
                              <Form.Group className="col-md-6 form-group">
                                <Form.Label>
                                  Inventory Type
                                  <span className="text-danger">*</span>
                                </Form.Label>
                                <Select
                                  options={[
                                    { value: "standard", label: "Standard" },
                                    { value: "serial", label: "Serial" },
                                    {
                                      value: "no_inventory",
                                      label: "No Inventory",
                                    },
                                  ]}
                                  name="inventoryType"
                                  id="inventoryType"
                                  value={[
                                    { value: "standard", label: "Standard" },
                                    { value: "serial", label: "Serial" },
                                    {
                                      value: "no_inventory",
                                      label: "No Inventory",
                                    },
                                  ].find(
                                    (option) =>
                                      option.value === values.inventoryType
                                  )}
                                  onChange={(option) =>
                                    setFieldValue(
                                      "inventoryType",
                                      option ? option.value : "standard"
                                    )
                                  }
                                  onBlur={handleBlur}
                                  placeholder="Select Inventory Type..."
                                  styles={{
                                    control: (provided) => ({
                                      ...provided,
                                      color: "#5e667b",
                                    }),
                                  }}
                                />
                                <ErrorMessage
                                  name="inventoryType"
                                  component="div"
                                  className="text-danger"
                                />
                              </Form.Group>
                              <Form.Group className="col-md-6 form-group">
                                <Form.Label htmlFor="model">
                                  Model
                                  <span className="text-danger">*</span>
                                </Form.Label>
                                <InputGroup>
                                  <Form.Control
                                    id="model"
                                    type="text"
                                    name="model"
                                    placeholder="Enter model"
                                    value={values.model}
                                    onChange={(e) => {
                                      handleChange(e);
                                         if (values.model === values.sku || values.sku === "") {
                                            setFieldValue("sku", e.target.value);
                                          }
                                    }}
                                    onBlur={handleBlur}
                                  />
                                </InputGroup>
                                <ErrorMessage
                                  name="model"
                                  component="div"
                                  className="text-danger"
                                />
                                {errors?.model?.map((error, index) => (
                                  <span
                                    key={index}
                                    className="w-100 mt-1 small text-danger"
                                  >
                                    {error}
                                    <br />
                                  </span>
                                ))}
                              </Form.Group>

                              <InputField
                                className="col-md-6 form-group"
                                type="text"
                                name="sku"
                                placeholder="Enter SKU"
                                value={values.sku}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                asterick={true}
                                errorMessage={errors?.sku}
                                label="SKU"
                              />

                              <Form.Group className="col-md-6 form-group">
                                <Form.Label>
                                  Manufacturer
                                  <span className="text-danger">*</span>
                                </Form.Label>
                                <Select
                                  closeMenuOnScroll={(e) => {
                                    try {
                                      const target = e.target;

                                      if (
                                        !target ||
                                        !target.nodeType ||
                                        typeof target.closest !== "function"
                                      ) {
                                        return true;
                                      }

                                      const isMenuScroll =
                                        target.closest('[class*="MenuList"]') ||
                                        target.closest('[class*="menu"]');

                                      return !isMenuScroll;
                                    } catch (error) {
                                      return true;
                                    }
                                  }}
                                  options={optionsManufacturer || []}
                                  as="select"
                                  name="manufacturerId"
                                  id="manufacturerId"
                                  data-style="py-0"
                                  value={
                                    optionsManufacturer &&
                                      optionsManufacturer.length > 0
                                      ? optionsManufacturer.find(
                                        (option) =>
                                          option.value ===
                                          values.manufacturerId
                                      )
                                      : null
                                  }
                                  onChange={(option) =>
                                    setFieldValue(
                                      "manufacturerId",
                                      option ? option.value : ""
                                    )
                                  }
                                  onBlur={handleBlur}
                                  placeholder="Select a Manufacturer..."
                                  isSearchable
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  menuPlacement="auto"
                                  styles={{
                                    menu: (provided) => ({
                                      ...provided,
                                      maxHeight: "200px",
                                      overflowY: "auto",
                                    }),
                                    menuList: (provided) => ({
                                      ...provided,
                                      maxHeight: "200px",
                                      overflowY: "auto",
                                    }),
                                    control: (provided) => ({
                                      ...provided,
                                      color: "#5e667b",
                                    }),
                                  }}
                                />

                                <ErrorMessage
                                  name="manufacturerId"
                                  component="div"
                                  className="text-danger"
                                />
                                {errors?.manufacturerId?.map((error, index) => (
                                  <span
                                    key={index}
                                    className="w-100 mt-1 small text-danger"
                                  >
                                    {error}
                                    <br />
                                  </span>
                                ))}
                              </Form.Group>

                              <Form.Group className="col-md-6 form-group">
                                <Form.Label>
                                  Vendor<span className="text-danger">*</span>
                                </Form.Label>
                                <Select
                                  closeMenuOnScroll={(e) => {
                                    try {
                                      const target = e.target;

                                      if (
                                        !target ||
                                        !target.nodeType ||
                                        typeof target.closest !== "function"
                                      ) {
                                        return true;
                                      }

                                      const isMenuScroll =
                                        target.closest('[class*="MenuList"]') ||
                                        target.closest('[class*="menu"]');

                                      return !isMenuScroll;
                                    } catch (error) {
                                      return true;
                                    }
                                  }}
                                  options={optionsVendor || []}
                                  as="select"
                                  name="vendorId"
                                  id="vendorId"
                                  data-style="py-0"
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  menuPlacement="auto"
                                  value={
                                    optionsVendor && optionsVendor.length > 0
                                      ? optionsVendor.find(
                                        (option) =>
                                          option.value === values.vendorId
                                      )
                                      : null
                                  }
                                  onChange={(option) =>
                                    setFieldValue(
                                      "vendorId",
                                      option ? option.value : ""
                                    )
                                  }
                                  onBlur={handleBlur}
                                  placeholder="Select a Vendor..."
                                  isSearchable
                                  styles={{
                                    menu: (provided) => ({
                                      ...provided,
                                      maxHeight: "200px",
                                      overflowY: "auto",
                                    }),
                                    menuList: (provided) => ({
                                      ...provided,
                                      maxHeight: "200px",
                                      overflowY: "auto",
                                    }),
                                    control: (provided) => ({
                                      ...provided,
                                      color: "#5e667b",
                                    }),
                                  }}
                                />

                                <ErrorMessage
                                  name="vendorId"
                                  component="div"
                                  className="text-danger"
                                />
                                {errors?.vendorId?.map((error, index) => (
                                  <span
                                    key={index}
                                    className="w-100 mt-1 small text-danger"
                                  >
                                    {error}
                                    <br />
                                  </span>
                                ))}
                              </Form.Group>

                              <InputField
                                className="col-md-6 form-group"
                                type="text"
                                name="unitOfMeasure"
                                placeholder="Enter Unit of Measure"
                                value={values.unitOfMeasure}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                asterick={true}
                                errorMessage={errors?.unitOfMeasure}
                                label="Unit of Measure"
                              />

                              <InputField
                                className="col-md-6 form-group"
                                type="text"
                                name="cost"
                                placeholder="Enter cost"
                                value={values.cost}
                                onChange={(e) => {
                                  let value = e.target.value;
                                  if (
                                    /^(\d{0,10})(\.\d{0,2})?$/.test(value) ||
                                    value === ""
                                  ) {
                                    const [intPart, decimalPart] =
                                      value.split(".");
                                    if (intPart.length <= 10) {
                                      setFieldValue("cost", value);
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  let value = e.target.value;
                                  if (value.endsWith(".")) {
                                    setFieldValue("cost", value.slice(0, -1));
                                  }
                                  if (value.startsWith(".")) {
                                    setFieldValue("cost", "0" + value);
                                  }
                                  const num = parseFloat(e.target.value);
                                  if (!isNaN(num)) {
                                    setFieldValue("cost", num.toFixed(2));
                                  }
                                }}
                                asterick={true}
                                errorMessage={errors?.cost}
                                label="Cost"
                                step="0.01"
                                pattern="^\d+(\.\d{0,2})?$"
                                min={0}
                              />

                              <Form.Group className="col-md-12 form-group">
                                <Form.Label htmlFor="phases">
                                  Phases<span className="text-danger">*</span>
                                </Form.Label>
                                <FieldArray
                                  name="phases"
                                  render={(arrayHelpers) => (
                                    <>
                                      <Row>
                                        {values.phases.map((ph, index) => {
                                          return (
                                            <Col md={6} key={`phase-${index}`} className="d-flex flex-row align-items-start mb-2">
                                              <div style={{ flex: 1 }}>
                                                <Select
                                                  options={optionsPhase.filter(
                                                    (option) =>
                                                      !values.phases.some(
                                                        (sel, selIdx) =>
                                                          sel === option.value && selIdx !== index
                                                      )
                                                  )}
                                                  name={`phases.${index}`}
                                                  value={
                                                    optionsPhase.find(
                                                      (option) => option.value === values.phases[index]
                                                    ) || null
                                                  }
                                                  onChange={(option) => {
                                                    setFieldValue(
                                                      `phases.${index}`,
                                                      option ? option.value : null
                                                    );
                                                  }}
                                                  onBlur={handleBlur}
                                                  placeholder="Select Phase..."
                                                  isClearable
                                                  styles={{
                                                    control: (provided) => ({
                                                      ...provided,
                                                      color: "#5e667b",
                                                    }),
                                                  }}
                                                />
                                                <ErrorMessage
                                                  name={`phases.${index}`}
                                                  component="div"
                                                  className="text-danger mt-1 small"
                                                />
                                              </div>
                                              {values.phases.length > 1 && (
                                                <Button
                                                  variant="danger"
                                                  className="d-flex align-items-center justify-content-center ms-2"
                                                  onClick={() => arrayHelpers.remove(index)}
                                                >
                                                  <RxCross2
                                                    style={{ width: "22px", height: "22px" }}
                                                    color="white"
                                                  />
                                                </Button>
                                              )}
                                            </Col>
                                          );
                                        })}
                                      </Row>
                                      <Row className="mb-3">
                                        <Col md={12}>
                                          <Button
                                            type="button"
                                            variant="btn btn-primary"
                                            size="md"
                                            disabled={values.phases.length >= 3}
                                            onClick={() => arrayHelpers.push(null)}
                                            className="d-flex align-items-center mt-2"
                                          >
                                            <FaPlus className="me-2" size={14} />
                                            Add more Phase
                                          </Button>
                                        </Col>
                                      </Row>
                                    </>
                                  )}
                                />
                              </Form.Group>

                            </div>
                          </div>
                        </div>

                        <div className="col-12">
                          <h5 className="text-primary mb-3">
                            Optional Information
                          </h5>

                          <div className="row">
                            <InputField
                              className="col-md-12 form-group"
                              as="textarea"
                              name="description"
                              placeholder="Enter Description"
                              value={values.description}
                              asterick={false}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              errorMessage={errors?.description}
                              label="Description"
                            />


                            <Form.Group className="col-12 form-group">
                              <Form.Label htmlFor="productPictures">
                                Product Images
                              </Form.Label>

                              <Field name="productPictures">
                                {({ field, form }) => (
                                  <DropFileInput field={field} form={form} />
                                )}
                              </Field>

                              <ErrorMessage
                                name="productPictures"
                                component="div"
                                className="text-danger"
                              />
                              {errors?.productPictures?.map((error, index) => (
                                <span
                                  key={index}
                                  className="w-100 mt-1 small text-danger"
                                >
                                  {error}
                                  <br />
                                </span>
                              ))}
                            </Form.Group>

                            <Form.Group className="col-md-6 form-group">
                              <Form.Label>Category</Form.Label>
                              <Select
                                closeMenuOnScroll={(e) => {
                                  try {
                                    const target = e.target;

                                    if (
                                      !target ||
                                      !target.nodeType ||
                                      typeof target.closest !== "function"
                                    ) {
                                      return true;
                                    }

                                    const isMenuScroll =
                                      target.closest('[class*="MenuList"]') ||
                                      target.closest('[class*="menu"]');

                                    return !isMenuScroll;
                                  } catch (error) {
                                    return true;
                                  }
                                }}
                                options={options || []}
                                as="select"
                                name="categoryId"
                                id="categoryId"
                                data-style="py-0"
                                value={
                                  options && options.length > 0
                                    ? options.find(
                                      (option) =>
                                        option.value === values.categoryId
                                    )
                                    : null
                                }
                                onChange={(option) =>
                                  setFieldValue(
                                    "categoryId",
                                    option ? option.value : ""
                                  )
                                }
                                onBlur={handleBlur}
                                placeholder="Select a Category..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuPlacement="auto"
                                isSearchable
                                isClearable
                                styles={{
                                  menu: (provided) => ({
                                    ...provided,
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }),
                                  menuList: (provided) => ({
                                    ...provided,
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }),
                                  control: (provided) => ({
                                    ...provided,
                                    color: "#5e667b",
                                  }),
                                }}
                              />

                              <ErrorMessage
                                name="categoryId"
                                component="div"
                                className="text-danger"
                              />
                              {errors?.categoryId?.map((error, index) => (
                                <span
                                  key={index}
                                  className="w-100 mt-1 small text-danger"
                                >
                                  {error}
                                  <br />
                                </span>
                              ))}
                            </Form.Group>

                            <InputField
                              className="col-md-6 form-group"
                              type="text"
                              name="upcNumber"
                              placeholder="Enter UPC number"
                              value={values.upcNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              errorMessage={errors?.upcNumber}
                              label="UPC Number"
                              max={12}
                              min={10}
                            />

                            <InputField
                              className="col-md-6 form-group"
                              type="text"
                              name="msrp"
                              placeholder="Enter MSRP"
                              value={values.msrp}
                              onChange={(e) => {
                                let value = e.target.value;
                                if (
                                  /^(\d{0,10})(\.\d{0,2})?$/.test(value) ||
                                  value === ""
                                ) {
                                  const [intPart, decimalPart] =
                                    value.split(".");
                                  if (intPart.length <= 10) {
                                    setFieldValue("msrp", value);
                                  }
                                }
                              }}
                              onBlur={(e) => {
                                let value = e.target.value;
                                if (value.endsWith(".")) {
                                  setFieldValue("msrp", value.slice(0, -1));
                                }
                                if (value.startsWith(".")) {
                                  setFieldValue("msrp", "0" + value);
                                }
                                const num = parseFloat(e.target.value);
                                if (!isNaN(num)) {
                                  setFieldValue("msrp", num.toFixed(2));
                                }
                              }}
                              errorMessage={errors?.msrp}
                              label="MSRP"
                              step="0.01"
                              pattern="^\d{1,10}(\.\d{1,2})?$"
                              min={0}
                            />

                            <InputField
                              className="col-md-6 form-group"
                              type="text"
                              name="labourPerHourPerunit"
                              placeholder="Enter Labour Per Hour Per Unit"
                              value={values.labourPerHourPerunit}
                              onChange={(e) => {
                                let value = e.target.value;
                                if (
                                  /^(\d{0,10})(\.\d{0,2})?$/.test(value) ||
                                  value === ""
                                ) {
                                  const [intPart, decimalPart] =
                                    value.split(".");
                                  if (intPart.length <= 10) {
                                    setFieldValue(
                                      "labourPerHourPerunit",
                                      value
                                    );
                                  }
                                }
                              }}
                              onBlur={(e) => {
                                let value = e.target.value;
                                if (value.endsWith(".")) {
                                  setFieldValue(
                                    "labourPerHourPerunit",
                                    value.slice(0, -1)
                                  );
                                }
                                if (value.startsWith(".")) {
                                  setFieldValue(
                                    "labourPerHourPerunit",
                                    "0" + value
                                  );
                                }
                                const num = parseFloat(e.target.value);
                                if (!isNaN(num)) {
                                  setFieldValue(
                                    "labourPerHourPerunit",
                                    num.toFixed(2)
                                  );
                                }
                              }}
                              errorMessage={errors?.labourPerHourPerunit}
                              label="Labour Per Hour Per Unit"
                              step="0.01"
                              pattern="^\d+(\.\d{0,2})?$"
                              min={0}
                            />

                            <InputField
                              className="col-md-6 form-group"
                              type="text"
                              name="rmu"
                              placeholder="Enter RMU"
                              value={values.rmu}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              errorMessage={errors?.rmu}
                              label="RMU"
                            />

                            <InputField
                              className="col-md-6 form-group"
                              type="text"
                              name="backbox"
                              placeholder="Enter Backbox"
                              value={values.backbox}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              errorMessage={errors?.backbox}
                              label="Backbox"
                            />

                            <InputField
                              className="col-md-6 form-group"
                              type="text"
                              name="weight"
                              placeholder="Enter Weight"
                              value={values.weight}
                              onChange={(e) => {
                                let value = e.target.value;
                                if (
                                  /^(\d{0,10})(\.\d{0,2})?$/.test(value) ||
                                  value === ""
                                ) {
                                  const [intPart, decimalPart] =
                                    value.split(".");
                                  if (intPart.length <= 10) {
                                    setFieldValue("weight", value);
                                  }
                                }
                              }}
                              onBlur={(e) => {
                                let value = e.target.value;
                                if (value.endsWith(".")) {
                                  setFieldValue("weight", value.slice(0, -1));
                                }
                                if (value.startsWith(".")) {
                                  setFieldValue("weight", "0" + value);
                                }
                                const num = parseFloat(e.target.value);
                                if (!isNaN(num)) {
                                  setFieldValue("weight", num.toFixed(2));
                                }
                              }}
                              errorMessage={errors?.weight}
                              label="Weight"
                              step="0.01"
                              pattern="^\d+(\.\d{0,2})?$"
                              min={0}
                            />

                            <Row>
                              <Form.Label htmlFor="technicalSpecs">
                                Technical Specifications{" "}
                              </Form.Label>
                              <FieldArray
                                name="technicalSpecs"
                                render={(arrayHelpers) => (
                                  <>
                                    {values.technicalSpecs.map(
                                      (spec, index) => (
                                        <React.Fragment key={index}>
                                          <Col
                                            md={6}
                                            className="d-flex flex-row align-items-start mb-3"
                                          >
                                            <Col md={5}>
                                              <InputField
                                                className="form-group"
                                                type="text"
                                                name={`technicalSpecs.${index}.key`}
                                                placeholder="Key"
                                                value={spec.key}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                errorMessage={
                                                  errors?.technicalSpecs?.[
                                                    index
                                                  ]?.key
                                                }
                                              />
                                            </Col>

                                            <Col
                                              md={5}
                                              className="d-flex flex-row"
                                            >
                                              <InputField
                                                className="form-group"
                                                type="text"
                                                name={`technicalSpecs.${index}.value`}
                                                placeholder="Value"
                                                value={spec.value}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                errorMessage={
                                                  errors?.technicalSpecs?.[
                                                    index
                                                  ]?.value
                                                }
                                              />
                                              {values.technicalSpecs.length >
                                                1 &&
                                                index !== 0 && (
                                                  <Button
                                                    variant="danger"
                                                    className="d-flex align-items-center justify-content-center ms-2"
                                                    style={{
                                                      marginTop: "1.5rem",
                                                      marginBottom: "auto",
                                                    }}
                                                    onClick={() =>
                                                      arrayHelpers.remove(index)
                                                    }
                                                  >
                                                    <RxCross2
                                                      style={{
                                                        width: "22px",
                                                        height: "22px",
                                                      }}
                                                      color="white"
                                                    />
                                                  </Button>
                                                )}
                                              {index === 0 && (
                                                <Button
                                                  variant="primary"
                                                  style={{
                                                    marginTop: "1.5rem",
                                                    marginBottom: "auto",
                                                  }}
                                                  disabled={
                                                    values.technicalSpecs
                                                      .length > 4
                                                  }
                                                  className="d-flex align-items-center justify-content-center ms-2"
                                                  onClick={() =>
                                                    arrayHelpers.push({
                                                      key: "",
                                                      value: "",
                                                    })
                                                  }
                                                >
                                                  <FaPlus
                                                    style={{
                                                      width: "22px",
                                                      height: "22px",
                                                    }}
                                                    color="white"
                                                  />
                                                </Button>
                                              )}
                                            </Col>
                                          </Col>
                                        </React.Fragment>
                                      )
                                    )}
                                  </>
                                )}
                              />
                            </Row>

                            <Row className="mt-4">
                              <Form.Label htmlFor="dimensions">
                                Dimensions
                              </Form.Label>
                              <FieldArray
                                name="dimensions"
                                render={(arrayHelpers) => (
                                  <>
                                    {values.dimensions.map((dim, index) => (
                                      <React.Fragment key={index}>
                                        <Col
                                          md={6}
                                          className="d-flex flex-row align-items-start mb-3"
                                        >
                                          <Col md={5}>
                                            <InputField
                                              className="form-group"
                                              type="text"
                                              name={`dimensions.${index}.key`}
                                              placeholder="Key"
                                              value={dim.key}
                                              onChange={handleChange}
                                              onBlur={handleBlur}
                                              errorMessage={
                                                errors?.dimensions?.[index]?.key
                                              }
                                            />
                                          </Col>

                                          <Col
                                            md={5}
                                            className="d-flex flex-row"
                                          >
                                            <InputField
                                              className="form-group"
                                              type="text"
                                              name={`dimensions.${index}.value`}
                                              placeholder="Value"
                                              value={dim.value}
                                              onChange={handleChange}
                                              onBlur={handleBlur}
                                              errorMessage={
                                                errors?.dimensions?.[index]
                                                  ?.value
                                              }
                                            />
                                            {values.dimensions.length > 1 &&
                                              index !== 0 && (
                                                <Button
                                                  variant="danger"
                                                  className="d-flex align-items-center justify-content-center ms-2 "
                                                  style={{
                                                    marginTop: "1.5rem",
                                                    marginBottom: "auto",
                                                  }}
                                                  onClick={() =>
                                                    arrayHelpers.remove(index)
                                                  }
                                                >
                                                  <RxCross2
                                                    style={{
                                                      width: "22px",
                                                      height: "22px",
                                                    }}
                                                    color="white"
                                                  />
                                                </Button>
                                              )}
                                            {index === 0 && (
                                              <Button
                                                variant="primary"
                                                style={{
                                                  marginTop: "1.5rem",
                                                  marginBottom: "auto",
                                                }}
                                                disabled={
                                                  values.dimensions.length > 4
                                                }
                                                className="d-flex align-items-center justify-content-center ms-2"
                                                onClick={() =>
                                                  arrayHelpers.push({
                                                    key: "",
                                                    value: "",
                                                  })
                                                }
                                              >
                                                <FaPlus
                                                  style={{
                                                    width: "22px",
                                                    height: "22px",
                                                  }}
                                                  color="white"
                                                />
                                              </Button>
                                            )}
                                          </Col>
                                        </Col>
                                      </React.Fragment>
                                    ))}
                                  </>
                                )}
                              />
                            </Row>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card.Body>

                  <Card.Footer>
                    <div className="d-flex flex-wrap flex-md-nowrap justify-content-end gap-1 gap-md-2">
                      <Button
                        type="button"
                        className="me-2 me-md-5 px-3 px-md-4"
                        variant="btn btn-light"
                        onClick={() => handleReset(resetForm)} // Reset the form
                      >
                        Reset
                      </Button>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        variant="btn btn-primary"
                        className="me-2 me-md-5 px-3 px-md-4"
                      >
                        Submit
                      </Button>
                      <Button
                        type="button"
                        className=" px-3 px-md-4"
                        variant="btn btn-danger"
                        onClick={() => navigate("/dashboard/app/product-list")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </Card.Footer>
                </Card>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ProductAdd;
